import { AccountantService, ClientService } from "../../../../services";
import {
	BackdropCircularProgress,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	formatDate,
	savePdfAsFile,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount
} from "../../../../common/Common";
import {
	setSelectedAccountant,
	setSelectedAccountantName,
	setSelectedActivityLogActions,
	setSelectedClient,
	setSelectedClientName,
	setSelectedStickyNavBar,
} from "../../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ErrorMessageSetupFormFields } from "../InProcessCollection/Panels/ErrorMessageSetupFormFields";
import { IAppState } from "../../../../store/reducers";
import { IDropDownListResponse } from "../../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../../models";
import { InProcessPaymentTransacitonsDataGrid } from "./InProcessPaymentTransacitonsDataGrid";
import { InProcessPaymentTransacitonsHeader } from "./InProcessPaymentTransacitonsHeader";
import { InProcessPaymentTransacitonsToolbar } from "./InProcessPaymentTransacitonsToolbar";
import { TransactionsServices } from "../../../../services/DirectDeposit/TransactionsServices";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { useGetErrorMessagesDDL } from "../InProcessCollection";
import { useNavigate } from "react-router-dom";

export interface IInProcessPaymentTransacitonsSetupPage { }
const IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY = "inProcessPayment-transactions";
const CLIENT_DDL_DATA_QUERY_KEY = "client-ddl-data-record";
const ACCOUNTANT_DDL_DATA_QUERY_KEY = "accountant-ddl-data-record";

const useInProcessPaymentTransacitons = (
	runGetListAPICall: boolean,
	accountant: string,
	client: string,
	clientType: string | number,
	type: string,
	transactionMethod: string | number,
	dateAppliesOn: string | number,
	startDate: string | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY,
			runGetListAPICall,
			accountant,
			client,
			clientType,
			type,
			transactionMethod,
			dateAppliesOn,
			params,
		],
		queryFn: () =>
			TransactionsServices.getDirectDepositForPaymentList(
				runGetListAPICall,
				800,
				accountant,
				client,
				clientType,
				type,
				transactionMethod,
				dateAppliesOn,
				startDate,
				endDate,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useGetAccountantDDLItemsItems = () =>
	useQuery({
		queryKey: [ACCOUNTANT_DDL_DATA_QUERY_KEY],
		queryFn: (data) => {
			return AccountantService.getGetAccountantDDL();
		},
		refetchOnWindowFocus: false,
	});
const useGetClientsDDLItems = (accountantId: string) =>
	useQuery({
		queryKey: [CLIENT_DDL_DATA_QUERY_KEY, accountantId],
		queryFn: (data) => {
			return ClientService.getGetClientDDL(accountantId);
		},
		refetchOnWindowFocus: false,
	});


export const InProcessPaymentTransacitons: React.FC<IInProcessPaymentTransacitonsSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [buttonTag, setButtonTag] = useState<any>(null);
	const [dialogButtonLabel, setDialogButtonLabel] = useState("");
	const [dialogContent, setDialogContent] = useState<any>(null);

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedRows, setSelectedRows] = useState([]);
	const [selectedSubRows, setSelectedSubRows] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [accountantId, setAccountantId] = useState("all");
	const [inProcessPaymentTransacitonsData, setInProcessPaymentTransacitonsData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);

	const [clearAllSelectedItems, setClearAllSelectedItems] = useState(0);
	const clearAllDataGridRowsSelections = useCallback(() => {
		setClearAllSelectedItems((prev) => prev + 1);
		setSelectedRows([]);
		setSelectedSubRows([]);
	}, []);

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	let {
		accountantStored,
		clientStored,
		clientTypeStored,
		typeStored,
		transactionMethodStored,
		dateAppliesOnStored,
		startDateStored,
		endDateStored,
	} = useSelector((state: IAppState) => {
		return {
			accountantStored: state.transactionsHeaders.accountant,
			clientStored: state.transactionsHeaders.client,
			clientTypeStored: state.transactionsHeaders.clientType,
			typeStored: state.transactionsHeaders.type,
			transactionMethodStored: state.transactionsHeaders.transactionMethod,
			dateAppliesOnStored: state.transactionsHeaders.dateAppliesOn,
			startDateStored: state.transactionsHeaders.startDate,
			endDateStored: state.transactionsHeaders.endDate,
		};
	});


	const accountant = formMethods.getValues("accountant");
	const client = formMethods.getValues("client");
	const clientType = formMethods.getValues("clientType");
	const type = formMethods.getValues("type");
	const transactionMethod = formMethods.getValues("transactionMethod");
	const dateAppliesOn = formMethods.getValues("dateAppliesOn");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");

	useEffect(() => {
		if (accountantStored && accountantStored !== accountant) {
			formMethods.setValue('accountant', accountantStored)
		}
		if (clientStored && clientStored !== client) {
			formMethods.setValue('client', clientStored)
		}
		if (clientTypeStored && clientTypeStored !== clientType) {
			formMethods.setValue('clientType', clientTypeStored)
		}
		if (typeStored && typeStored !== type) {
			formMethods.setValue('type', typeStored)
		}
		if (dateAppliesOnStored && dateAppliesOnStored !== dateAppliesOn) {
			formMethods.setValue('dateAppliesOn', dateAppliesOnStored)
		}
		if (transactionMethodStored && transactionMethodStored !== transactionMethod) {
			formMethods.setValue('transactionMethod', transactionMethodStored)
		}
		if (startDateStored && startDateStored !== startDate) {
			let date = new Date(startDateStored)
			formMethods.setValue('startDate', date)
		}
		if (endDateStored && endDateStored !== endDate) {
			let date = new Date(endDateStored)
			formMethods.setValue('endDate', date)
		}
		// formMethods.setValue('accountant', accountantStored)
		// formMethods.setValue('client', clientStored)
		// formMethods.setValue('clientType', clientTypeStored)
		// formMethods.setValue('type', typeStored)
		// formMethods.setValue('dateAppliesOn', dateAppliesOnStored)
		// formMethods.setValue('startDate', startDateStored)
		// formMethods.setValue('endDate', endDateStored)
	}, [accountantStored, clientStored, clientTypeStored, typeStored, transactionMethodStored, dateAppliesOnStored, startDateStored, endDateStored])


	const { isPending, data } = useInProcessPaymentTransacitons(
		runGetListAPICall,
		accountantStored && accountantStored,
		clientStored && clientStored,
		clientTypeStored && clientTypeStored,
		typeStored && typeStored,
		transactionMethodStored && transactionMethodStored,
		dateAppliesOnStored && dateAppliesOnStored,
		startDateStored && startDateStored,
		endDateStored && endDateStored,
		queryParams,
		navigateToLogout
	);
	const InProcessPaymentTransacitonsList: any | null = data;

	useEffect(() => {
		if (InProcessPaymentTransacitonsList) {
			setInProcessPaymentTransacitonsData(InProcessPaymentTransacitonsList);
		}
	}, [InProcessPaymentTransacitonsList]);

	const { isPending: isProcessing, mutate: saveInProcessPaymentTransactions } =
		useMutation({
			mutationFn: TransactionsServices.updateDirectDepositForPaymentCollections,
			onSuccess: (res: IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					// let path = `/company/departments/setup/${res.data.id}`;
					// navigate(path);
					// return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
					// }),
					// queryClient.invalidateQueries({
					// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					// }),
					// ]);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
				} else if (res.status === 201) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					// let path = `/company/departments/setup/${res.data.id}`;
					// navigate(path);
					// setEditRecordId(res.data.id);
					// return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
					// }),
					// queryClient.invalidateQueries({
					// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					// }),
					// ]);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isDeleting, mutate: deleteInProccessPaymentTransactions } =
		useMutation({
			mutationFn: TransactionsServices.updateDirectDepositForCollections,
			onSuccess: (res: IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					// let path = `/company/departments/setup/${res.data.id}`;
					// navigate(path);
					// return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
					// }),
					// queryClient.invalidateQueries({
					// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					// }),
					// ]);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
				} else if (res.status === 201) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					// let path = `/company/departments/setup/${res.data.id}`;
					// navigate(path);
					// setEditRecordId(res.data.id);
					// return Promise.all([
					// queryClient.invalidateQueries({
					// 	queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
					// }),
					// queryClient.invalidateQueries({
					// 	queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					// }),
					// ]);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingAccountantDDLItemsItems, data: accountantDDL } =
		useGetAccountantDDLItemsItems();
	const { isPending: isPendingClientsDDLItems, data: clientsDDL } =
		useGetClientsDDLItems(accountantId);
	const { isPending: isPendingErrorMessageDDLItems, data: errorMessagesDDL } =
		useGetErrorMessagesDDL();

	const [updatedErrorMessagesDDL, setUpdatedErrorMessagesDDL] = useState(errorMessagesDDL)
	useEffect(() => {
		if (updatedErrorMessagesDDL && errorMessagesDDL) {
			if (errorMessagesDDL.length !== updatedErrorMessagesDDL.length) {
				queryClient.invalidateQueries({ queryKey: ['error-message-ddl'] })
			}
		}
	}, [updatedErrorMessagesDDL])

	//   after click apply button api calls with updated values and update employee report data
	// const InProcessPaymentTransacitonsDataAPICall = (
	// 	clientIdStore: string,
	// ) => {
	// 	const fetchData = async () => {
	// 		setIsLoading(true);
	// 		setQueryParams({
	// 			search_query: "",
	// 			order_by: "firstName",
	// 			order_direction: "asc",
	// 			page: 1,
	// 			size: 25,
	// 		});
	// 		try {
	// 			const response: any = await ReportsService.getInProcessPaymentTransacitons(
	// 				clientIdStore,
	// 				{
	// 					search_query: "",
	// 					order_by: "firstName",
	// 					order_direction: "asc",
	// 					page: 1,
	// 					size: 25,
	// 				},
	// 				navigateToLogout
	// 			);

	// 			if (response) {
	// 				setInProcessPaymentTransacitonsData(response);
	// 			}
	// 		} catch {
	// 		} finally {
	// 			setIsLoading(false);
	// 		}
	// 	};
	// 	fetchData();
	// };

	const accountantDDLItems = useMemo(() => {
		if (accountantDDL) {
			if (
				accountantDDL &&
				Array.isArray(accountantDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(accountantDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [accountantDDL]);

	const clientsDDLItems = useMemo(() => {
		if (clientsDDL) {
			if (
				clientsDDL &&
				Array.isArray(clientsDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(clientsDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [clientsDDL]);

	const onAccountantSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false)
		setAccountantId(event.value)
	}

	//   after click apply button api calls with updated values and update employee report data
	const inProcessPaymentTransacitonsDataAPICall = (
		accountant: string,
		client: string,
		clientType: string,
		type: string,
		transactionMethod: string | number,
		dateAppliesOn: string,
		startDate: string | null,
		endDate: string | null,
	) => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const response: any = await TransactionsServices.getDirectDepositForPaymentList(
					true,
					800,
					accountant,
					client,
					clientType,
					type,
					transactionMethod,
					dateAppliesOn,
					formatDate(startDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setInProcessPaymentTransacitonsData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		inProcessPaymentTransacitonsDataAPICall(
			values.accountant,
			values.client,
			values.clientType,
			values.type,
			values.transactionMethod,
			values.dateAppliesOn,
			values.startDate,
			values.endDate,
		);
	};

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const onClickHandler = async (clickedRow: any, screenTag: string) => {
		setIsLoading(true);
		try {
			const accountantDetails = await AccountantService.getById(
				clickedRow.accountantId,
				navigateToLogout
			);

			if (accountantDetails && accountantDetails.id) {
				dispatch(setSelectedAccountant({ id: accountantDetails.id }));
				dispatch(setSelectedAccountant(accountantDetails));
				dispatch(setSelectedAccountantName({ accTitle: accountantDetails.accTitle }));

				const clientDetails = await ClientService.getById(clickedRow.clientId, navigateToLogout);

				if (clientDetails && clientDetails.id) {
					dispatch(
						setSelectedClient({
							...clientDetails,
							superUserFieldsShownInClientSetup: true,
						})
					);
					dispatch(setSelectedClientName({ clientName: clientDetails.legalName }));
					if (screenTag === 'employee') navigate(`/employee/setup/${clickedRow.employeeId}`)
					if (screenTag === 'client') navigate("/clients/dashboard");
				}
			} else {
				const response = await ClientService.getById(clickedRow.clientId, navigateToLogout);

				if (response && response.id) {
					dispatch(setSelectedClient(response));
					if (screenTag === 'employee') navigate(`/employee/setup/${clickedRow.employeeId}`)
					if (screenTag === 'client') navigate("/clients/dashboard");
				}
			}
		} catch (error) {
			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}
	};


	const upadtedInProcessPaymentTransactionsListItems = useMemo(() => {
		return (
			inProcessPaymentTransacitonsData &&
			inProcessPaymentTransacitonsData.items &&
			inProcessPaymentTransacitonsData?.items.map((item: any) => ({
				...item,
				clientName: (
					<span
						// to={`/clients/dashboard`}
						className="no-underline"
						style={{ color: theme.palette.primary.light, cursor: "pointer" }}
						onClick={() => onClickHandler(item, "client")}
					>
						{item.clientName}
					</span>
				),
				orders: item.orders &&
					item.orders.map((item: any) => ({
						...item,
						employeeName: (
							<span
								// to={`/clients/dashboard`}
								className="no-underline"
								style={{ color: theme.palette.primary.light, cursor: "pointer" }}
								onClick={() => onClickHandler(item, "employee")}
							>
								{item.employeeName}
							</span>
						),
					}))
			}))
		);
	}, [inProcessPaymentTransacitonsData]);

	// const updatedTotalSelectedRows = (rows: any, subRows: any) => {
	// 	const rowsWhichParentNotSelected = rows && rows.reduce((acc: any, parent: any) => {
	// 		const parentOrderIds = parent.orders.map((o: any) => o.id);

	// 		const allOrdersPresent = parentOrderIds.every((orderId: any) =>
	// 			acc.some((nk: any) => nk.id === orderId)
	// 		);

	// 		if (allOrdersPresent) {
	// 			// remove all matching children
	// 			return acc.filter((nk: any) => !parentOrderIds.includes(nk.id));
	// 		}

	// 		return acc;
	// 	}, subRows);

	// 	let rowsWhichParentNotSelectedIds = rowsWhichParentNotSelected.map((item: any) => item.id);
	// 	let selectedRowsIds = selectedRows.map((item: any) => item.id);
	// 	let totalRowsSelected = [...rowsWhichParentNotSelected, ...selectedRows]
	// 	let totalRowsSelectedIds = [...rowsWhichParentNotSelectedIds, ...selectedRowsIds]

	// 	return totalRowsSelected
	// }

	// const onStartProcessingButtonClickedHandler = () => {
	// 	// const totalSelectedRows = updatedTotalSelectedRows(upadtedInProcessPaymentTransactionsListItems, selectedSubRows)

	// }
	const onCreatePaymentFileButtonClickedHandler = () => {
		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("create-payment-file")
			setDialogButtonLabel("Create Payment File")
			setDialogContent(`Are you sure you want to create payment file of (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to process!",
				severity: "error"
			})
		}
	}
	const onDeleteButtonClickedHandler = () => {
		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("delete")
			setDialogButtonLabel("Delete")
			setDialogContent(`Are you sure you want to delete (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to delete!",
				severity: "error"
			})
		}
	}
	const onSaveButtonClickedHandler = () => {
		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("save")
			setDialogButtonLabel("Save")
			setDialogContent(`Are you sure you want to save (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to permanent delete!",
				severity: "error"
			})
		}
	}
	const onUndoButtonClickedHandler = () => {
		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("undo")
			setDialogButtonLabel("Undo")
			setDialogContent(`Are you sure you want to undo (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to permanent delete!",
				severity: "error"
			})
		}
	}
	const onProccessedButtonClickedHandler = () => {
		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("proccessed")
			setDialogButtonLabel("Proccessed")
			setDialogContent(`Are you sure you want to proccessed (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to permanent delete!",
				severity: "error"
			})
		}
	}
	const onErrorInTransactionButtonClickedHandler = () => {
		if (selectedSubRows.length !== 0) {
			setDialogOpen(true)
			setButtonTag("error-in-transaction")
			setDialogButtonLabel("Error in Transaction")
			setDialogContent(`Are you sure you want to error in transaction of (${selectedSubRows.length}) selected ${selectedSubRows.length === 1 ? 'record' : 'records'}?`)
		} else {
			setSnackbarData({
				open: true,
				message: "Please select at least one row to permanent delete!",
				severity: "error"
			})
		}
	}
	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const dialogButtonClickedHandler = async (tag: string | null) => {
		if (tag === 'save') {
			// const totalSelectedRows = updatedTotalSelectedRows(upadtedInProcessPaymentTransactionsListItems, selectedRows)
			// saveInProcessPaymentTransactions(selectedSubRows)
		} else if (tag === 'delete') {
			// const totalSelectedRows = updatedTotalSelectedRows(upadtedInProcessPaymentTransactionsListItems, selectedRows);
			// 	(async () => {
			// 		try {
			// 			setIsLoading(true)
			// 			const response: any =
			// 				await TransactionsServices.deleteInProcessPaymentTransactions(selectedSubRows);
			// 			queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
			// 		} catch { } finally { setIsLoading(false) }
			// 	})();

			const updatePayloadData =
				selectedSubRows.map((item: any) => ({
					...item,
					lastTab: 800,
					fromLastTabOn: 600,
					// this line add after making clientname clickable 
					clientName: item.clientName.props ? item.clientName.props.children : item.clientName,
					employeeName: item.employeeName.props ? item.employeeName.props.children : item.employeeName,
				}));
			// deleteInProccessPaymentTransactions(updatePayloadData)
			saveInProcessPaymentTransactions(updatePayloadData)
			setDialogOpen(false)
		} else if (tag === 'create-payment-file') {
			// const totalSelectedRows = updatedTotalSelectedRows(upadtedInProcessPaymentTransactionsListItems, selectedRows);
			// (async () => {
			// 	try {
			// 		setIsLoading(true)
			// 		const response: any =
			// 			await TransactionsServices.createPaymentFileInProcessPaymentTransactions(selectedSubRows);
			// 		queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
			// 	} catch { } finally { setIsLoading(false) }
			// })();

			const selectedRowsIds = selectedSubRows.map((x: any) => x.id);
			(async () => {
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.createPaymentFileInProcessPaymentTransactions(selectedRowsIds);
					if (response) {
						if (response.status === 400) {
							setSnackbarData({
								open: true,
								message: response.data.message
									? response.data.message
									: "Something went wrong.",
								severity: "error",
							});
						} else {
							const date = new Date()
							const filename = `${formatDate(date)} SP DEBIT.txt`;
							savePdfAsFile(response, filename);

						}
						setDialogOpen(false)
					}
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
					queryClient.invalidateQueries({ queryKey: ['error-message-ddl'] })
				} catch { } finally { clearAllDataGridRowsSelections(); setIsLoading(false); }
			})();
		} else if (tag === 'undo') {
			// const totalSelectedRows = updatedTotalSelectedRows(upadtedInProcessPaymentTransactionsListItems, selectedRows);
			(async () => {
				try {
					setIsLoading(true)
					const response: any =
						await TransactionsServices.undoInProcessPaymentTransactions(selectedSubRows);
					queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
				} catch { } finally { setIsLoading(false) }
			})();
		} else if (tag === 'proccessed') {
			// const totalSelectedRows = updatedTotalSelectedRows(upadtedInProcessPaymentTransactionsListItems, selectedRows);
			// (async () => {
			// 	try {
			// 		setIsLoading(true)
			// 		const response: any =
			// 			await TransactionsServices.proccessedInProcessPaymentTransactions(selectedSubRows);
			// 		queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
			// 	} catch { } finally { setIsLoading(false) }
			// })();

			const hasUngeneratedFile = selectedRows.some((item: any) => !item.fileGenerated);
			if (hasUngeneratedFile) {
				setDialogOpen(false)
				const message = "Payment file not created for checkboxes selected with red background.";
				const followUp = "Create Payment File to proceed.";

				alert(`${message}\n${followUp}`);
			} else {
				const updatePayloadData =
					selectedSubRows.map((item: any) => ({
						...item,
						lastTab: 800,
						fromLastTabOn: 200,
						// this line add after making clientname clickable 
						clientName: item.clientName.props ? item.clientName.props.children : item.clientName,
						employeeName: item.employeeName.props ? item.employeeName.props.children : item.employeeName,
					}));
				saveInProcessPaymentTransactions(updatePayloadData)
				setDialogOpen(false)
			}
		} else if (tag === 'error-in-transaction') {
			// const totalSelectedRows = updatedTotalSelectedRows(upadtedInProcessPaymentTransactionsListItems, selectedRows);
			// (async () => {
			// 	try {
			// 		setIsLoading(true)
			// 		const response: any =
			// 			await TransactionsServices.errorInTransactionInProcessPaymentTransactions(selectedSubRows);
			// 		queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
			// 	} catch { } finally { setIsLoading(false) }
			// })();

			const updatePayloadData =
				selectedSubRows.map((item: any) => ({
					...item,
					transactionErrorId: item.errorMessage,
					errorMessage: errorMessagesDDL && errorMessagesDDL.find((x: any) => x.value === item.errorMessage)?.label,
					lastTab: 800,
					fromLastTabOn: 1300,
					// this line add after making clientname clickable 
					clientName: item.clientName.props ? item.clientName.props.children : item.clientName,
					employeeName: item.employeeName.props ? item.employeeName.props.children : item.employeeName,
				}));
			saveInProcessPaymentTransactions(updatePayloadData)
			setDialogOpen(false)
		}
	}
	const onSaveErrorMessageClickedHandler = (values: any) => {
		// (async () => {
		// 	try {

		// 		setIsLoading(true)
		// 		const response: any =
		// 			await TransactionsServices.saveErrorMessageInProcessPaymentTransactions(values);
		// 		queryClient.invalidateQueries({ queryKey: ['error-message-ddl'] })
		// 		queryClient.invalidateQueries({ queryKey: [IN_PROCESS_PAYMENT_TRANSACTIONS_QUERY_KEY] })
		// 	} catch { } finally { setIsLoading(false) }
		// })();
	}
	const onCloseErrorMessageHandler = () => {
		setDialogOpen(false)
	}
	const onAddErrorMessageButtonClickedHandler = () => {
		setDialogOpen(true)
		setButtonTag("add-error-message")
		setDialogButtonLabel("Save")
		setDialogContent(
			<ErrorMessageSetupFormFields errorMessagesDDL={sortDDLByLabel(errorMessagesDDL)}
				updatedErrorMessagesDDL={(value: any) => setUpdatedErrorMessagesDDL(value)}
				onSaveErrorMessageClickedHandler={onSaveErrorMessageClickedHandler}
				onCloseErrorMessageHandler={onCloseErrorMessageHandler}
			/>
		)
	}
	const expandedLabels = ["Transactions", "Payment"];
	const expandedInProcessPaymentOrSetupItem = "In Process Payment";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Payment" &&
				item.label === expandedInProcessPaymentOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedInProcessPaymentOrSetupItem && "/direct-deposit/transactions/in-process-payment",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);



	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"In Process Payment"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<InProcessPaymentTransacitonsHeader
						clientsDDL={clientsDDLItems}
						accountantDDL={accountantDDLItems}
						onAccountantSelectionChangeHandler={onAccountantSelectionChangeHandler}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)} />
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isLoading ||
						isPendingClientsDDLItems ||
						isProcessing ||
						isPendingErrorMessageDDLItems
					}
					testId="inProcessPayment-transactions"
				/>


				<InProcessPaymentTransacitonsDataGrid
					columns={
						inProcessPaymentTransacitonsData?.headerColumns
							? inProcessPaymentTransacitonsData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
					}
					rows={
						inProcessPaymentTransacitonsData && upadtedInProcessPaymentTransactionsListItems
							? upadtedInProcessPaymentTransactionsListItems
							: []
						// inProcessPaymentTransacitonsData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={true}
					showSettingColumn={false}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						inProcessPaymentTransacitonsData && inProcessPaymentTransacitonsData.total
							? inProcessPaymentTransacitonsData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search..."}
					showRowExpandedView={true}
					selectedRows={(value: any) => setSelectedRows(value)}
					selectedSubRows={(value: any) => setSelectedSubRows(value)}
					onAddErrorMessageButtonClickedHandler={onAddErrorMessageButtonClickedHandler}
					errorMessagesDDL={errorMessagesDDL}
					clearAllSelectedItems={clearAllSelectedItems}
				/>
				{inProcessPaymentTransacitonsData && inProcessPaymentTransacitonsData.items && inProcessPaymentTransacitonsData.items.length > 0 && (
					<InProcessPaymentTransacitonsToolbar
						onCreatePaymentFileButtonClickedHandler={onCreatePaymentFileButtonClickedHandler}
						onDeleteButtonClickedHandler={onDeleteButtonClickedHandler}
						onSaveButtonClickedHandler={onSaveButtonClickedHandler}
						onUndoButtonClickedHandler={onUndoButtonClickedHandler}
						onProccessedButtonClickedHandler={onProccessedButtonClickedHandler}
						onErrorInTransactionButtonClickedHandler={onErrorInTransactionButtonClickedHandler}
					/>
				)}
			</Grid>

			<EnhancedDialog
				dialogTitle={
					buttonTag === 'pay-period-payment-date' ? "Pay Period Setup" :
						buttonTag === 'add-error-message' ? "Error Message Setup" :
							"Please Confirm"}
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseHandler}
				open={openDialog}
				dialogContent={dialogContent}
				showdividers={false}
				dialogAction={
					buttonTag === 'add-error-message' ?
						<></> :
						<Grid>
							<EnhancedButtonPrimeReact
								label="Cancel"
								severity="secondary"
								text
								raised
								onClickHandler={onCloseHandler}
								style={{ fontSize: "small" }}
							/>
							<EnhancedButtonPrimeReact
								label={dialogButtonLabel}
								severity={
									buttonTag === 'pay-period-payment-date' ? EnhancedButtonPrimeReactSeverity.Success :
										buttonTag === 'add-error-message' ? EnhancedButtonPrimeReactSeverity.Success :
											buttonTag === 'create-debit-file' ? EnhancedButtonPrimeReactSeverity.Success :
												buttonTag === 'create-payment-file' ? EnhancedButtonPrimeReactSeverity.Success :
													buttonTag === 'delete' ? EnhancedButtonPrimeReactSeverity.Danger :
														buttonTag === 'save' ? EnhancedButtonPrimeReactSeverity.Success :
															buttonTag === 'proccessed' ? EnhancedButtonPrimeReactSeverity.Success :
																buttonTag === 'upload' ? EnhancedButtonPrimeReactSeverity.Success :
																	EnhancedButtonPrimeReactSeverity.Secondary}
								raised
								onClickHandler={() => dialogButtonClickedHandler(buttonTag)}
								style={{ marginLeft: "16px", fontSize: "small" }}
							/>
						</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
